import React, { useState, useEffect, useRef, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, AlertCircle, History } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Status options
const STATUS_OPTIONS = [
  { value: 'yes', label: 'Yes / Fully implemented', score: 2 },
  { value: 'partial', label: 'Partially implemented', score: 1 },
  { value: 'no', label: 'No / Not implemented', score: 0 },
  { value: 'unknown', label: 'Unknown', score: 0 },
  { value: 'not_applicable', label: 'Not applicable', score: 2 }
];

// Client Name Autocomplete - engagement field only.
// Fetches distinct client names (once, cached on the component instance) and
// suggests matches as the consultant types. Selecting a suggestion writes the
// exact stored spelling so pre-fill lookup + trend matching stay aligned.
const ClientNameAutocomplete = ({ value, onChange, prompt, required }) => {
  const { authFetch } = useAuth();
  const [clients, setClients] = useState([]);  // [{client_name, count, last_updated}]
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);

  // Fetch client list once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/assessments/clients');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) setClients(data);
      } catch (err) {
        // Non-fatal: autocomplete is a nice-to-have; user can still type freely
        console.warn('Client-name autocomplete lookup failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  // Filter suggestions on every render — cheap for <1000 clients
  const suggestions = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (clients.length === 0) return [];
    if (!q) return clients.slice(0, 8);
    // Exact-match case: don't show a dropdown suggesting what the user has already picked
    const exact = clients.find(c => c.client_name.toLowerCase() === q);
    if (exact && clients.filter(c => c.client_name.toLowerCase().startsWith(q)).length === 1) {
      return [];
    }
    return clients
      .filter(c => c.client_name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [clients, value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectSuggestion = (name) => {
    onChange(name);
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIdx].client_name);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toISOString().split('T')[0]; } catch { return ''; }
  };

  return (
    <div className="space-y-2" ref={wrapRef}>
      <Label className="text-sm font-medium text-slate-900">
        {prompt}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div className="relative">
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIdx(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={prompt}
          className="rounded-xl"
          autoComplete="off"
          data-testid="client-name-input"
        />
        {open && suggestions.length > 0 && (
          <div
            className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
            data-testid="client-name-suggestions"
          >
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Existing clients {value?.trim() ? `matching "${value.trim()}"` : ''}
            </div>
            {suggestions.map((c, i) => (
              <button
                key={c.client_name}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(c.client_name); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={cn(
                  'w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors',
                  i === activeIdx ? 'bg-slate-100' : 'hover:bg-slate-50'
                )}
                data-testid={`client-suggestion-${c.client_name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <History className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="truncate font-medium text-slate-900">{c.client_name}</span>
                </div>
                <div className="shrink-0 flex items-center gap-2 text-[11px] text-slate-500">
                  <span>{c.count} assessment{c.count === 1 ? '' : 's'}</span>
                  {c.last_updated && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span>last {fmtDate(c.last_updated)}</span>
                    </>
                  )}
                </div>
              </button>
            ))}
            <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-500">
              Or keep typing to create a new client
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



// Multi-Select Matrix Component - for policy coverage questions
const MultiSelectMatrixQuestion = ({ question, value, onChange }) => {
  const { domains, states, scoring, helperText } = question;
  
  // Get current selections - keyed by domain name
  const currentSelections = value?.selections || {};
  
  // Count how many domains are answered
  const answeredCount = Object.keys(currentSelections).length;
  const totalDomains = domains?.length || 0;
  const allAnswered = answeredCount === totalDomains;
  
  // Calculate aggregate score as a percentage (0-100)
  const calculateAggregateScore = (selections) => {
    const domainList = domains || [];
    if (domainList.length === 0) return 0;
    
    let totalScore = 0;
    let answeredDomains = 0;
    
    domainList.forEach(domain => {
      const selectedState = selections[domain];
      if (selectedState && scoring && scoring[selectedState] !== undefined) {
        totalScore += scoring[selectedState];
        answeredDomains++;
      }
    });
    
    // Return average percentage score
    if (answeredDomains === 0) return 0;
    return Math.round(totalScore / answeredDomains);
  };
  
  const handleStateChange = (domain, newState) => {
    const updatedSelections = {
      ...currentSelections,
      [domain]: newState
    };
    
    const aggregateScore = calculateAggregateScore(updatedSelections);
    // Convert percentage to 0-2 scale for scoring engine compatibility
    const normalizedScore = aggregateScore >= 85 ? 2 : aggregateScore >= 50 ? 1 : 0;
    const status = normalizedScore === 2 ? 'yes' : normalizedScore === 1 ? 'partial' : 'no';
    
    onChange({
      ...value,
      selections: updatedSelections,
      score: normalizedScore,
      aggregatePercentage: aggregateScore,
      status,
      answer: updatedSelections,
      answeredCount: Object.keys(updatedSelections).length,
      totalDomains
    });
  };
  
  // Get color for state based on scoring
  const getStateColor = (state) => {
    const score = scoring?.[state];
    if (score === undefined) return 'bg-slate-100';
    if (score >= 80) return 'bg-green-100 border-green-300';
    if (score >= 50) return 'bg-amber-100 border-amber-300';
    if (score >= 20) return 'bg-orange-100 border-orange-300';
    return 'bg-red-100 border-red-300';
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-sm font-medium leading-6 text-slate-900">
          {question.prompt}
        </Label>
        {helperText && (
          <p className="text-xs text-slate-500">{helperText}</p>
        )}
        <div className="flex items-center gap-2 mt-2">
          <span className={cn(
            "text-xs font-medium px-2 py-1 rounded-full",
            allAnswered ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
          )}>
            {answeredCount} / {totalDomains} policies assessed
          </span>
          {value?.aggregatePercentage !== undefined && (
            <span className={cn(
              "text-xs font-medium px-2 py-1 rounded-full",
              value.aggregatePercentage >= 85 ? "bg-green-100 text-green-700" :
              value.aggregatePercentage >= 50 ? "bg-amber-100 text-amber-700" :
              "bg-red-100 text-red-700"
            )}>
              Score: {value.aggregatePercentage}%
            </span>
          )}
        </div>
      </div>
      
      {/* Matrix Table */}
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="text-left p-3 font-medium text-slate-700 min-w-[200px]">Policy Domain</th>
              {states?.map((state, idx) => (
                <th key={idx} className="text-center p-2 font-medium text-slate-700 min-w-[100px] text-xs">
                  {state}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {domains?.map((domain, rowIdx) => {
              const selectedState = currentSelections[domain];
              const isAnswered = !!selectedState;
              
              return (
                <tr 
                  key={rowIdx} 
                  className={cn(
                    "border-b last:border-0 transition-colors",
                    isAnswered ? "bg-slate-50/50" : "bg-white",
                    !isAnswered && "hover:bg-slate-50"
                  )}
                >
                  <td className="p-3 font-medium text-slate-800 flex items-center gap-2">
                    {isAnswered ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
                    )}
                    <span className="text-sm">{domain}</span>
                  </td>
                  {states?.map((state, colIdx) => {
                    const isSelected = selectedState === state;
                    return (
                      <td key={colIdx} className="p-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleStateChange(domain, state)}
                          className={cn(
                            "w-full px-2 py-2 rounded-lg text-xs font-medium transition-all",
                            "border hover:scale-105",
                            isSelected 
                              ? cn(getStateColor(state), "ring-2 ring-slate-400 ring-offset-1")
                              : "bg-white border-slate-200 hover:border-slate-300 text-slate-600"
                          )}
                        >
                          {isSelected && "✓"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-600 pt-2">
        <span className="font-medium">Legend:</span>
        {states?.map((state, idx) => (
          <span key={idx} className="flex items-center gap-1">
            <span className={cn("w-3 h-3 rounded", getStateColor(state))}></span>
            {state} ({scoring?.[state]}%)
          </span>
        ))}
      </div>
      
      {/* Notes */}
      <Textarea
        value={value?.notes || ''}
        onChange={(e) => onChange({ ...value, notes: e.target.value })}
        placeholder="Optional notes for consultant reference"
        className="min-h-[80px] text-sm"
      />
    </div>
  );
};

// Status Question Component
const StatusQuestion = ({ question, value, onChange }) => {
  const isReverse = question.type === 'statusReverse';
  const isGatingControl = question.gating_rule === true;
  
  // Determine ideal answer for gating control
  // For regular questions: 'yes' is ideal
  // For reverse questions: 'no' is ideal
  const idealAnswer = isReverse ? 'no' : 'yes';
  
  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium leading-6 text-slate-900">
        {question.prompt}
        {isReverse && <span className="text-xs text-slate-500 ml-2">(Reverse scored)</span>}
      </Label>
      <RadioGroup 
        value={value?.status || ''} 
        onValueChange={(v) => {
          const option = STATUS_OPTIONS.find(o => o.value === v);
          let score = option?.score || 0;
          // For reverse questions, invert the score
          if (isReverse) {
            score = v === 'yes' ? 0 : v === 'partial' ? 1 : v === 'no' ? 2 : v === 'not_applicable' ? 2 : 0;
          }
          onChange({ ...value, status: v, score });
        }} 
        className="grid gap-2 sm:grid-cols-2"
      >
        {STATUS_OPTIONS.map((option) => {
          const isIdealAnswer = isGatingControl && option.value === idealAnswer;
          return (
            <div 
              key={option.value} 
              className={cn(
                'flex items-center space-x-2 rounded-xl border p-3 transition',
                value?.status === option.value && 'border-slate-900 bg-slate-50',
                isIdealAnswer && 'border-green-200'
              )}
            >
              <RadioGroupItem value={option.value} id={`${question.field_id}-${option.value}`} />
              <Label 
                htmlFor={`${question.field_id}-${option.value}`} 
                className="cursor-pointer text-sm flex-1 flex items-center gap-2"
              >
                {option.label}
                {isIdealAnswer && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                )}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
      <Textarea
        value={value?.notes || ''}
        onChange={(e) => onChange({ ...value, notes: e.target.value })}
        placeholder="Optional notes for consultant reference"
        className="min-h-[80px] text-sm"
      />
    </div>
  );
};

// Main Field Renderer Component
export default function FieldRenderer({ 
  question, 
  value, 
  onChange, 
  isEngagementField = false 
}) {
  // Handle engagement fields differently (simpler structure)
  if (isEngagementField) {
    const fieldType = question.type;
    
    if (fieldType === 'text' || fieldType === 'number' || fieldType === 'date') {
      // Special case: client_name field opts into autocomplete against
      // existing clients so consultants can't create near-duplicates.
      if (fieldType === 'text' && question.autocomplete_source === 'clients') {
        return (
          <ClientNameAutocomplete
            value={value}
            onChange={onChange}
            prompt={question.prompt}
            required={question.required}
          />
        );
      }
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.prompt}
            {question.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Input
            type={fieldType}
            value={value || ''}
            onChange={(e) => onChange(fieldType === 'number' ? Number(e.target.value || 0) : e.target.value)}
            placeholder={question.prompt}
            className="rounded-xl"
          />
        </div>
      );
    }

    if (fieldType === 'textarea') {
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">{question.prompt}</Label>
          <Textarea 
            value={value || ''} 
            onChange={(e) => onChange(e.target.value)} 
            className="min-h-[100px] rounded-xl"
            placeholder={question.prompt}
          />
        </div>
      );
    }

    if (fieldType === 'select') {
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.prompt}
            {question.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className="rounded-xl">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {question.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return null;
  }

  // Regular questionnaire fields
  const type = question.type;

  // Multi-select matrix questions (policy coverage)
  if (type === 'multi_select_matrix') {
    return <MultiSelectMatrixQuestion question={question} value={value} onChange={onChange} />;
  }

  // Status questions
  if (type === 'status' || type === 'statusReverse') {
    return <StatusQuestion question={question} value={value} onChange={onChange} />;
  }

  // Text input
  if (type === 'text' || type === 'number' || type === 'date') {
    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium leading-6 text-slate-900">
          {question.prompt}
        </Label>
        <Input
          type={type}
          value={value?.answer || value?.value || ''}
          onChange={(e) => {
            const val = type === 'number' ? Number(e.target.value || 0) : e.target.value;
            onChange({ ...value, answer: val, value: val, score: val ? 2 : 0 });
          }}
          placeholder={question.placeholder || question.prompt}
          className="rounded-xl"
        />
        {question.weight > 0 && (
          <Textarea
            value={value?.notes || ''}
            onChange={(e) => onChange({ ...value, notes: e.target.value })}
            placeholder="Optional notes"
            className="min-h-[60px] text-sm"
          />
        )}
      </div>
    );
  }

  // Textarea
  if (type === 'textarea') {
    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium leading-6 text-slate-900">
          {question.prompt}
        </Label>
        <Textarea 
          value={value?.answer || value?.value || ''} 
          onChange={(e) => onChange({ ...value, answer: e.target.value, value: e.target.value, score: e.target.value ? 2 : 0 })} 
          className="min-h-[120px] rounded-xl"
          placeholder={question.placeholder || question.prompt}
        />
      </div>
    );
  }

  // Select dropdown
  if (type === 'select') {
    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium leading-6 text-slate-900">
          {question.prompt}
        </Label>
        <Select 
          value={value?.answer || value?.value || ''} 
          onValueChange={(v) => {
            // Find the option to get score if available
            const option = question.options?.find(o => o.value === v);
            const score = option?.score ?? (v && v !== 'unknown' && v !== 'none' ? 2 : 0);
            onChange({ ...value, answer: v, value: v, score });
          }}
        >
          <SelectTrigger className="rounded-xl">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {question.options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {question.weight > 0 && (
          <Textarea
            value={value?.notes || ''}
            onChange={(e) => onChange({ ...value, notes: e.target.value })}
            placeholder={question.notesPlaceholder || "Optional notes"}
            className="min-h-[60px] text-sm"
          />
        )}
      </div>
    );
  }

  // Select with explicit scores
  if (type === 'selectScored') {
    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium leading-6 text-slate-900">
          {question.prompt}
        </Label>
        {question.help_text && (
          <p className="text-xs text-slate-500 leading-relaxed -mt-1" data-testid={`help-text-${question.field_id}`}>
            {question.help_text}
          </p>
        )}
        <Select
          value={value?.answer || value?.value || ''}
          onValueChange={(v) => {
            const selected = question.options?.find((opt) => opt.value === v);
            const score = selected?.score ?? 0;
            const status = score === 2 ? 'yes' : score === 1 ? 'partial' : 'no';
            onChange({ ...value, answer: v, value: v, score, status });
          }}
        >
          <SelectTrigger className="rounded-xl">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {question.options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {question.weight > 0 && (
          <Textarea
            value={value?.notes || ''}
            onChange={(e) => onChange({ ...value, notes: e.target.value })}
            placeholder="Optional notes"
            className="min-h-[60px] text-sm"
          />
        )}
      </div>
    );
  }

  // Multiselect with checkboxes
  if (type === 'multiselect') {
    const currentValues = Array.isArray(value?.answer) 
      ? value.answer 
      : Array.isArray(value?.value) 
      ? value.value 
      : [];
    
    // Mutually exclusive options (selecting one clears all others)
    const mutuallyExclusiveOptions = ['none', 'unknown'];
    
    // Check if "Other" is selected and needs details
    const hasOther = currentValues.includes('other');
    const otherDetails = value?.otherDetails || '';
    const otherDetailsRequired = hasOther && question.options?.some(o => o.value === 'other');
    const otherDetailsValid = !otherDetailsRequired || otherDetails.trim().length > 0;
    
    // Calculate score based on selections
    const calculateScore = (selections, details) => {
      // Very low maturity indicators
      if (selections.length === 0) return { score: 0, status: 'no' };
      if (selections.length === 1 && selections[0] === 'none') return { score: 0, status: 'no' };
      if (selections.length === 1 && selections[0] === 'unknown') return { score: 0, status: 'no' };
      
      // Low maturity indicators - these don't count toward positive scoring
      const lowMaturityOptions = ['none', 'unknown', 'internal_policies_only', 'other'];
      
      // High-value selections for defence opportunities
      const highValueDefenceOptions = [
        'existing_direct_contract',
        'existing_subcontract', 
        'bidding_direct',
        'bidding_subcontractor',
        'responding_rft_rfp'
      ];
      
      // High-value selections for compliance frameworks
      const highValueComplianceOptions = [
        'essential_eight_ml2',
        'ism',
        'disp',
        'iso_27001',
        'soc_2',
        'nist_csf',
        'nist_800_171_cmmc',
        'cis_controls'
      ];
      
      // Filter out low maturity selections for scoring
      const meaningfulSelections = selections.filter(v => !lowMaturityOptions.includes(v));
      const hasOnlyLowMaturity = selections.length > 0 && meaningfulSelections.length === 0;
      
      // Check for high-value selections
      const hasHighValueDefence = selections.some(v => highValueDefenceOptions.includes(v));
      const hasHighValueCompliance = selections.some(v => highValueComplianceOptions.includes(v));
      const hasHighValue = hasHighValueDefence || hasHighValueCompliance;
      
      let score;
      if (hasOnlyLowMaturity) {
        // "Other" alone with valid details gets partial score
        if (selections.includes('other') && details?.trim()) {
          score = 1;
        } else {
          score = 0;
        }
      } else if (hasHighValue || meaningfulSelections.length >= 3) {
        score = 2;
      } else {
        score = 1;
      }
      const status = score === 2 ? 'yes' : score === 1 ? 'partial' : 'no';
      return { score, status };
    };

    const handleCheckboxChange = (optValue, next) => {
      let updated;
      
      if (next) {
        // Adding a selection
        if (mutuallyExclusiveOptions.includes(optValue)) {
          // If selecting a mutually exclusive option, clear all others
          updated = [optValue];
        } else {
          // If selecting a regular option, remove any mutually exclusive options
          updated = [...currentValues.filter(v => !mutuallyExclusiveOptions.includes(v)), optValue];
        }
      } else {
        // Removing a selection
        updated = currentValues.filter((item) => item !== optValue);
      }
      
      const { score, status } = calculateScore(updated, value?.otherDetails);
      
      // Clear otherDetails if "other" is deselected
      const newOtherDetails = updated.includes('other') ? value?.otherDetails : '';
      
      onChange({ 
        ...value, 
        answer: updated, 
        value: updated, 
        score, 
        status,
        otherDetails: newOtherDetails,
        isValid: !updated.includes('other') || (newOtherDetails?.trim().length > 0)
      });
    };

    const handleOtherDetailsChange = (details) => {
      const { score, status } = calculateScore(currentValues, details);
      onChange({ 
        ...value, 
        otherDetails: details,
        score,
        status,
        isValid: details.trim().length > 0
      });
    };
    
    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium leading-6 text-slate-900">
          {question.prompt}
        </Label>
        <div className="grid gap-2 sm:grid-cols-2">
          {question.options?.map((opt) => {
            const checked = currentValues.includes(opt.value);
            const isMutuallyExclusive = mutuallyExclusiveOptions.includes(opt.value);
            const isDisabledByExclusive = !isMutuallyExclusive && 
              currentValues.some(v => mutuallyExclusiveOptions.includes(v));
            
            return (
              <div 
                key={opt.value} 
                className={cn(
                  'flex items-center space-x-3 rounded-xl border p-3 transition',
                  checked && 'border-slate-900 bg-slate-50',
                  isDisabledByExclusive && 'opacity-50'
                )}
              >
                <Checkbox
                  id={`${question.field_id}-${opt.value}`}
                  checked={checked}
                  disabled={isDisabledByExclusive}
                  onCheckedChange={(next) => handleCheckboxChange(opt.value, next)}
                />
                <Label 
                  htmlFor={`${question.field_id}-${opt.value}`}
                  className={cn(
                    "text-sm cursor-pointer flex-1",
                    isDisabledByExclusive && "cursor-not-allowed"
                  )}
                >
                  {opt.label}
                  {isMutuallyExclusive && (
                    <span className="text-xs text-slate-400 ml-1">(exclusive)</span>
                  )}
                </Label>
              </div>
            );
          })}
        </div>
        
        {/* Conditional "Other" details field */}
        {hasOther && (
          <div className="space-y-2 pl-4 border-l-2 border-slate-200">
            <Label className="text-sm font-medium text-slate-700">
              Please specify the other framework(s) <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              value={otherDetails}
              onChange={(e) => handleOtherDetailsChange(e.target.value)}
              placeholder="Enter framework name(s)..."
              className={cn(
                "rounded-xl",
                !otherDetailsValid && "border-red-300 focus:border-red-500"
              )}
            />
            {!otherDetailsValid && (
              <p className="text-xs text-red-500">
                Please specify the framework(s) when selecting "Other"
              </p>
            )}
          </div>
        )}
        
        {question.weight > 0 && (
          <Textarea
            value={value?.notes || ''}
            onChange={(e) => onChange({ ...value, notes: e.target.value })}
            placeholder={question.notesPlaceholder || "Optional notes"}
            className="min-h-[60px] text-sm"
          />
        )}
      </div>
    );
  }

  return null;
}
